import {validateGenderStep} from "../steps/Gender/utils";
import {validateBodyShapeBustStep} from "../steps/BodyShapeBust/utils";
import {validateBodyShapeWaistStep} from "../steps/BodyShapeWaist/utils";
import {validateBodyShapeHipStep} from "../steps/BodyShapeHip/utils";
import {validateTryOnStep} from "../steps/TryOn/utils";

export { capitalizeFirstLetter, capitalize } from "./capitalize";

export const getLang = () => {
  const ISO639Code = document.documentElement.lang;
  return ISO639Code?.toLowerCase() || "";
};

export const intCompare = (a, b) => (parseInt(a) > parseInt(b) ? 1 : -1);

export const sumArray = (array) => array.reduce((a, b) => a + b);

export const sortWithIndices = (toSort, compFunc) => {
  for (let i = 0; i < toSort.length; i++) {
    toSort[i] = [toSort[i], i];
  }
  toSort.sort(compFunc);
  toSort.sortIndices = [];
  for (let j = 0; j < toSort.length; j++) {
    toSort.sortIndices.push(toSort[j][1]);
    toSort[j] = toSort[j][0];
  }
  return toSort.sortIndices;
};

export const sortBodySizes = (allSizes) => {
  const sortRes = allSizes.map((size) =>
    sumArray(
      Object.keys(size).map((measure) =>
        measure[0] != "_" ? size[measure] : 0
      )
    )
  );
  return sortWithIndices(sortRes, intCompare).map((ind) => allSizes[ind]);
};

export const findNextBiggerSizeByMeasure = (
  allSizes,
  currentSize,
  modifiedMeasure
) => {
  let newSize = Object.assign({}, currentSize);
  newSize[modifiedMeasure] =
    (measureToFloat(currentSize[modifiedMeasure]) || 0) + 0.1;

  // const biggerSizes = allSizes.filter(productSize => !newSize[modifiedMeasure] || !productSize[modifiedMeasure] || measureToFloat(productSize[modifiedMeasure]) >= measureToFloat(newSize[modifiedMeasure]));
  // Old method that would not allow other measures to be smaller
  const modifiableMeasures = Object.keys(newSize).filter(
    (measure) => !measure.includes("__")
  );
  let biggerSizes = allSizes.filter((productSize) =>
    modifiableMeasures.every(
      (measure) =>
        !newSize[measure] ||
        !productSize[measure] ||
        measureToFloat(productSize[measure]) >= measureToFloat(newSize[measure])
    )
  );
  if (biggerSizes.length === 0) {
    biggerSizes = allSizes.filter(
      (productSize) =>
        !newSize[modifiedMeasure] ||
        !productSize[modifiedMeasure] ||
        measureToFloat(productSize[modifiedMeasure]) >=
          measureToFloat(newSize[modifiedMeasure])
    );
    if (biggerSizes.length === 0) {
      return currentSize;
    }
  }
  newSize = biggerSizes[0];
  let minMeasureValue = newSize[modifiedMeasure];
  biggerSizes.forEach((size) => {
    if (size[modifiedMeasure] < minMeasureValue) {
      newSize = size;
      minMeasureValue = size[modifiedMeasure];
    }
  });

  const nextSizes = biggerSizes.filter(
    (size) => size[modifiedMeasure] === minMeasureValue
  );
  const sortedSizes = nextSizes
    .map((size) => ({
      dist: modifiableMeasures
        .filter((measure) => size[measure])
        .map((measure) =>
          currentSize[measure] ? size[measure] - currentSize[measure] : 0
        )
        .reduce((a, b) => a + b, 0),
      size,
    }))
    .sort((a, b) => a.dist - b.dist);

  return sortedSizes[0].size;
};

export const findSizeByMeasures = (allSizes, size) => {
  const modifiableMeasures = Object.keys(size).filter(
    (measure) => !measure.includes("__")
  );
  const matchedSizes = allSizes.filter((productSize) =>
    modifiableMeasures.every(
      (measure) => !size[measure] || productSize[measure] === size[measure]
    )
  );
  return matchedSizes.length > 0 ? matchedSizes[0] : null;
};

export const findNextSmallerSizeByMeasure = (
  allSizes,
  currentSize,
  modifiedMeasure
) => {
  let newSize = Object.assign({}, currentSize);
  newSize[modifiedMeasure] =
    (measureToFloat(currentSize[modifiedMeasure]) || 0) - 0.1;
  // const smallerSizes = allSizes.filter(productSize => !newSize[modifiedMeasure] || !productSize[modifiedMeasure] || measureToFloat(productSize[modifiedMeasure]) <= measureToFloat(newSize[modifiedMeasure]));
  // Old method that would not allow other measures to be bigger
  const modifiableMeasures = Object.keys(newSize).filter(
    (measure) => !measure.includes("__")
  );
  let smallerSizes = allSizes.filter((productSize) =>
    modifiableMeasures.every(
      (measure) =>
        !newSize[measure] ||
        !productSize[measure] ||
        measureToFloat(productSize[measure]) <= measureToFloat(newSize[measure])
    )
  );
  if (smallerSizes.length === 0) {
    smallerSizes = allSizes.filter(
      (productSize) =>
        !newSize[modifiedMeasure] ||
        !productSize[modifiedMeasure] ||
        measureToFloat(productSize[modifiedMeasure]) <=
          measureToFloat(newSize[modifiedMeasure])
    );
    if (smallerSizes.length === 0) {
      return currentSize;
    }
  }
  newSize = smallerSizes[0];
  let maxMeasureValue = newSize[modifiedMeasure];
  smallerSizes.forEach((size) => {
    if (size[modifiedMeasure] > maxMeasureValue) {
      newSize = size;
      maxMeasureValue = size[modifiedMeasure];
    }
  });

  const nextSizes = smallerSizes.filter(
    (size) => size[modifiedMeasure] === maxMeasureValue
  );
  const sortedSizes = nextSizes
    .map((size) => ({
      dist: modifiableMeasures
        .filter((measure) => size[measure])
        .map((measure) =>
          currentSize[measure] ? currentSize[measure] - size[measure] : 0
        )
        .reduce((a, b) => a + b, 0),
      size,
    }))
    .sort((a, b) => a.dist - b.dist);

  return sortedSizes[0].size;
};

export const getViewedBodyMeasures = (bodySize) =>
  (bodySize
    ? Object.keys(bodySize).filter(
        (measure) =>
          bodySize[measure] != null &&
          !measure.includes("__") &&
          measure !== "id"
      )
    : []
  ).sort((a, b) => (a == "body_size" ? -1 : b == "body_size" ? 1 : 0));

export const measureToFloat = (measure) =>
  !!parseFloat(measure) ? parseFloat(measure) : measure.charCodeAt(0);

export const breastCupFloatToName = (cupSizeNum) =>
  String.fromCharCode(cupSizeNum).toUpperCase();

let originalViewPort = null;

const setViewPort = () => {
  if (!document.querySelector('meta[name="viewport"]')) {
    const viewPortMeta = document.createElement("meta");
    viewPortMeta.setAttribute("name", "viewport");
    viewPortMeta.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0"
    );
    document.head.appendChild(viewPortMeta);
    originalViewPort = null;
  } else {
    const viewPort = document.querySelector('meta[name="viewport"]');
    originalViewPort = viewPort.getAttribute("content");
    viewPort.setAttribute("content", "width=device-width, initial-scale=1.0");
  }
};

const resetViewPort = () => {
  const viewPort = document.querySelector('meta[name="viewport"]');
  if (originalViewPort !== null) {
    viewPort.setAttribute("content", originalViewPort);
    originalViewPort = null;
  } else {
    viewPort.parentNode.removeChild(viewPort);
  }
};

export const ciIncludes = (items, item) =>
  items.map((i) => i.toLowerCase()).includes(item);

export const isBodyShapeValid = (bodyShape) =>
  bodyShape && bodyShape.height && bodyShape.weight;

export const deepClone = (json) => JSON.parse(JSON.stringify(json));

export const deepAssign = (target, ...sources) => {
  sources.forEach((source) => {
    if (source) {
      Object.keys(source).forEach((key) => {
        const value = source[key];
        if (
          typeof value === "object" &&
          value !== undefined &&
          value !== null
        ) {
          if (
            target[key] !== undefined &&
            target[key] !== null &&
            typeof target[key] !== "object"
          ) {
            throw `Cannot assign object to non-object key (${key})`;
          }
          if (target[key] === undefined) {
            if (Object.prototype.toString.call(value) === "[object Array]") {
              target[key] = deepClone(value);
            } else {
              target[key] = {};
              deepAssign(target[key], value);
            }
          } else {
            if (Object.prototype.toString.call(value) === "[object Array]") {
              target[key] = deepClone(value);
            } else {
              deepAssign(target[key], value);
            }
          }
        } else {
          // if (
          //   target[key] !== undefined &&
          //   target[key] !== null &&
          //   typeof target[key] !== typeof value
          // ) {
          //   throw `Cannot assign different type to key (${key}). ${typeof target[
          //     key
          //   ]} <-- ${typeof value}`;
          // }
          target[key] = value;
        }
      });
    }
  });
  return target;
};

export const getOrderedKeys = (obj) =>
  obj
    ? (obj._order ? obj._order : Array.from(Object.keys(obj))).filter(
        (size) => size[0] !== "_"
      )
    : [];

// export const getProjection = (a, b, p) => {
//     const atob = {x: b.x - a.x, y: b.y - a.y};
//     const atop = {x: p.x - a.x, y: p.y - a.y};
//     const len = atob.x * atob.x + atob.y * atob.y;
//     let dot = atop.x * atob.x + atop.y * atob.y;
//     const t = Math.min(1, Math.max(0, dot / len));
//     return new Point(a.x + atob.x * t, a.y + atob.y * t);
// }

export const classNames = (...classes) => {
  return classes
    .reduce((acc, c) => {
      const updateAcc = [...acc];
      if (typeof c === "string") {
        updateAcc.push(c);
      } else if (c && typeof c === "object") {
        const c1 = Object.keys(c).filter((k) => Boolean(c[k]));
        if (c1.length > 0) {
          updateAcc.push(...c1.map((k) => k));
        }
      }
      return updateAcc;
    }, [])
    .join(" ");
};

export const dataURItoBlob = (dataURI) => {
  if (dataURI) {
    // convert base64/URLEncoded data component to raw binary data held in a string
    var byteString;
    if (dataURI.split(",")[0].indexOf("base64") >= 0)
      byteString = atob(dataURI.split(",")[1]);
    else byteString = unescape(dataURI.split(",")[1]);

    // separate out the mime component
    var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

    // write the bytes of the string to a typed array
    var ia = new Uint8Array(byteString.length);
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }
};

export const changeStepStringToNumber = (string) => {
  // return null;
  if (string === "firstStep") {
    return null;
    // } else if (string === "lastStep") {
    //   return configInit.steps.length - 1;
  } else if (string === "chartSize") {
    return -2;
  }
};

export const detectActiveStep = (profile, product_gender, steps, measure_range) => {
  let goToStep = null;
  steps.forEach((step, index) => {
    if (goToStep === null) {
      if (step.id === "Gender") {
        if (!validateGenderStep(profile, product_gender)) {
          goToStep = index;
        }
      } else if (step.id === "BodyShapeBust") {
        if (!validateBodyShapeBustStep(profile, step.config)) {
          goToStep = index;
        }
      } else if (step.id === "BodyShapeWaist") {
        if (!validateBodyShapeWaistStep(profile, step.config)) {
          goToStep = index;
        }
      } else if (step.id === "BodyShapeHip") {
        if (!validateBodyShapeHipStep(profile, step.config)) {
          goToStep = index;
        }
      } else if (step.id === "TryOn") {
        if (!validateTryOnStep(profile, step.config)) {
          goToStep = index;
        }
      }
    }
  });

  return goToStep === null ? steps.length - 1 : goToStep;
};

export const params = new Proxy(new URLSearchParams(window.location.search), {
  get: (searchParams, prop) => searchParams.get(prop),
});
